import React, { useState } from 'react';
import {
  Button,
  CardContent,
  Collapse,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useReleaseTemplateBackups } from '../../../hooks/useReleaseTemplateBackups';
import { ConfirmDialog } from './ConfirmDialog';
import {
  ActionRow,
  Chevron,
  CollapsibleHeader,
  CollapsibleHeaderTitle,
  DangerOutlinedButton,
  Field,
  Section,
} from './styles';

// "Manage templates" card — occasional-use surface for restoring or
// refreshing the seed template. Owns its own collapse state + the two
// confirm dialogs for restore/delete; the underlying mutations come from
// `useReleaseTemplateBackups`.
export const ManageTemplatesCard: React.FC = () => {
  const backups = useReleaseTemplateBackups();
  // Collapse the template-management section by default — it's an
  // occasional-use area, not part of the day-to-day release workflow.
  const [expanded, setExpanded] = useState(false);

  const itemsSuffix = backups.selectedItems === 1 ? '' : 's';

  return (
    <Section>
      <CardContent>
        <CollapsibleHeader
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(v => !v)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded(v => !v);
            }
          }}
          aria-expanded={expanded}
          aria-controls="manage-templates-content"
        >
          <CollapsibleHeaderTitle variant="h6">
            Manage templates
          </CollapsibleHeaderTitle>
          <IconButton size="small" tabIndex={-1} aria-hidden>
            <Chevron open={expanded} />
          </IconButton>
        </CollapsibleHeader>
        <Collapse in={expanded} timeout="auto">
          <div id="manage-templates-content" style={{ marginTop: 12 }}>
            <Field variant="outlined" size="small">
              <InputLabel id="template-backup-select-label">
                Template backups
              </InputLabel>
              <Select
                labelId="template-backup-select-label"
                label="Template backups"
                value={backups.selected}
                onChange={e => backups.setSelected(e.target.value as string)}
              >
                {backups.options.length === 0 && (
                  <MenuItem value="" disabled>
                    <em>No backups yet</em>
                  </MenuItem>
                )}
                {backups.options.map(b => (
                  <MenuItem key={b.partition} value={b.partition}>
                    {b.partition} ({b.items} item{b.items === 1 ? '' : 's'})
                  </MenuItem>
                ))}
              </Select>
            </Field>
            <ActionRow style={{ marginBottom: 12 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={!backups.selected || backups.restorePending}
                onClick={backups.openConfirmRestore}
              >
                {backups.restorePending ? 'Restoring…' : 'Restore'}
              </Button>
              <DangerOutlinedButton
                size="small"
                variant="outlined"
                disabled={!backups.selected || backups.deletePending}
                onClick={backups.openConfirmDelete}
              >
                {backups.deletePending ? 'Deleting…' : 'Delete'}
              </DangerOutlinedButton>
            </ActionRow>
            <Typography
              variant="caption"
              color="textSecondary"
              component="p"
              style={{ marginBottom: 12 }}
            >
              Re-seeds the default checklist from the committed Confluence dump.
              Existing items are snapshotted before being replaced.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={backups.refreshPending}
              onClick={backups.refresh}
              fullWidth
            >
              {backups.refreshPending
                ? 'Refreshing…'
                : 'Refresh from Confluence'}
            </Button>
          </div>
        </Collapse>
      </CardContent>

      <ConfirmDialog
        open={backups.confirmRestoreOpen}
        title="Restore template backup?"
        body={
          <>
            Replace the live default template with{' '}
            <strong>{backups.selected}</strong> ({backups.selectedItems} item
            {itemsSuffix}). The backup itself stays intact, so you can restore
            again later if needed.
          </>
        }
        confirmLabel="Restore"
        confirmPendingLabel="Restoring…"
        pending={backups.restorePending}
        variant="primary"
        onClose={backups.closeConfirmRestore}
        onConfirm={backups.restore}
      />

      <ConfirmDialog
        open={backups.confirmDeleteOpen}
        title="Delete template backup?"
        body={
          <>
            Permanently delete <strong>{backups.selected}</strong> (
            {backups.selectedItems} item{itemsSuffix})? Other existing backups
            and the live default template are not affected.
          </>
        }
        confirmLabel="Delete"
        confirmPendingLabel="Deleting…"
        pending={backups.deletePending}
        variant="danger"
        onClose={backups.closeConfirmDelete}
        onConfirm={backups.deleteSelected}
      />
    </Section>
  );
};
